import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatCurrency = (val, currencyCode = 'CLP') => {
    const number = Math.round(Number(val)) || 0;
    const code = currencyCode.toUpperCase().trim();
    
    if (code === 'CLP') return `$ ${number.toLocaleString('es-CL')}`;
    if (code === 'PEN') return `S/. ${number.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    if (code === 'USD') return `US$ ${number.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    if (code === 'ARS') return `ARS ${number.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
    if (code === 'COP') return `COP$ ${number.toLocaleString('es-CO')}`;
    return `${code} ${number.toLocaleString()}`;
};

// Función auxiliar para cargar imagen y evitar errores de firma PNG
const loadImage = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = (e) => reject(e);
        img.src = url;
    });
};

export const generateSettlementPDF = async (settlement, trips, advances = []) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // DETERMINAR MONEDA (priorizar moneda de pago al proveedor/conductor antes que la moneda de cobro local)
    const currency = trips.length > 0 ? (trips[0].provider_currency_code || trips[0].local_currency_code || 'CLP') : 'CLP';
    
    // --- 1. CABECERA CON LOGO ---
    try {
        const img = await loadImage('/logoe.png');
        doc.addImage(img, 'PNG', pageWidth - 50, 12, 35, 18);
    } catch (e) {
        console.warn("Logo Emilia no pudo ser cargado desde /public/logoe.png:", e);
    }

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(31, 78, 120); // Azul EmiGo
    doc.text("COMPROBANTE DE PAGO CONDUCTOR", 14, 22);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Transportes Emilia SpA", 14, 28);
    
    // --- 2. CÁLCULOS Y RESUMEN ---
    let totalCostoBase = 0;
    let totalFacturarChofer = 0;
    let totalEfectivo = 0;
    let totalBaseSum = 0;
    let totalEsperaSum = 0;
    let totalPagoSum = 0;
    let totalComisionPctSum = 0;

    trips.forEach(t => {
        const waitCosto = parseFloat(t.waiting_time_provider_cost) || 0;
        const baseSinWait = currency !== 'CLP' ? (t.provider_cost_local || 0) : (t.provider_cost_base || 0);
        const cBase = baseSinWait + waitCosto;
        
        let cPago = t.driver_earnings || 0;
        const reimbursable = (t.extra_services || []).find(s => s.id === 'reimbursable_expense');
        if (reimbursable) {
            cPago += parseFloat(reimbursable.cost) || 0;
        }
        
        totalCostoBase += cBase;
        totalFacturarChofer += cPago;

        if (t.payment_method?.toLowerCase() === 'cash' || t.payment_method?.toLowerCase() === 'efectivo') {
            totalEfectivo += cBase; 
        }

        totalBaseSum += baseSinWait;
        totalEsperaSum += waitCosto;
        totalPagoSum += cPago;

        let comisionPct = 0;
        const totalCostoParaComision = baseSinWait + waitCosto;
        if (totalCostoParaComision > 0) {
            comisionPct = Math.round(((totalCostoParaComision - t.driver_earnings) / totalCostoParaComision) * 100);
        }
        totalComisionPctSum += comisionPct;
    });

    const advancesList = Array.isArray(advances) ? advances : [];
    const totalDescuentosAdv = advancesList.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
    const sumDescuentosPositivos = advancesList.reduce((sum, a) => {
        const amt = parseFloat(a.amount) || 0;
        return amt > 0 ? sum + amt : sum;
    }, 0);
    const sumAbonosPositivos = advancesList.reduce((sum, a) => {
        const amt = parseFloat(a.amount) || 0;
        return amt < 0 ? sum + Math.abs(amt) : sum;
    }, 0);

    const totalLiquido = (settlement.total_amount !== undefined && settlement.total_amount !== null)
        ? settlement.total_amount
        : Math.max(0, totalFacturarChofer - totalEfectivo - totalDescuentosAdv);
    const totalAnticipos = sumDescuentosPositivos > 0
        ? sumDescuentosPositivos
        : Math.max(0, totalFacturarChofer - totalEfectivo - totalLiquido);
    const totalAbonos = sumAbonosPositivos > 0
        ? sumAbonosPositivos
        : Math.max(0, totalLiquido - (totalFacturarChofer - totalEfectivo));
    const avgComision = trips.length > 0 ? Math.round(totalComisionPctSum / trips.length) : 0;

    const formatSettleDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return new Date(dateStr).toLocaleDateString('es-CL');
    };
    const startDate = formatSettleDate(settlement.period_start);
    const endDate = formatSettleDate(settlement.period_end);
    const folio = `LIQ-${String(settlement.id).padStart(5, '0')}`;
    const driverName = settlement.driver?.full_name?.toUpperCase() || "SISTEMA";

    doc.setFillColor(31, 78, 120);
    doc.rect(14, 35, pageWidth - 28, 8, 'F');
    doc.setFontSize(10); doc.setTextColor(255, 255, 255);
    doc.text("RESUMEN DE LIQUIDACIÓN FINANCIERA", 16, 40);

    doc.setDrawColor(200, 200, 200); doc.setFillColor(242, 242, 242);
    doc.rect(14, 43, pageWidth - 28, 35, 'FD');

    doc.setTextColor(0, 0, 0); doc.setFontSize(8);
    let resumenY = 49;
    doc.setFont("helvetica", "bold"); doc.text("FOLIO:", 18, resumenY);
    doc.setFont("helvetica", "normal"); doc.text(folio, 45, resumenY);
    doc.setFont("helvetica", "bold"); doc.text("MONEDA:", 100, resumenY);
    doc.setFont("helvetica", "normal"); doc.text(currency, 145, resumenY);

    resumenY += 6;
    doc.setFont("helvetica", "bold"); doc.text("CONDUCTOR:", 18, resumenY);
    doc.setFont("helvetica", "normal"); doc.text(driverName, 45, resumenY);
    doc.setFont("helvetica", "bold"); doc.text("PRODUCCIÓN (BASE):", 100, resumenY);
    doc.setFont("helvetica", "normal"); doc.text(formatCurrency(totalCostoBase, currency), 145, resumenY);

    resumenY += 6;
    doc.setFont("helvetica", "bold"); doc.text("PERIODO:", 18, resumenY);
    doc.setFont("helvetica", "normal"); doc.text(`${startDate} al ${endDate}`, 45, resumenY);
    doc.setFont("helvetica", "bold"); doc.text("TOTAL A FACTURAR:", 100, resumenY);
    doc.setFont("helvetica", "normal"); doc.text(formatCurrency(totalFacturarChofer, currency), 145, resumenY);

    resumenY += 6;
    doc.setFont("helvetica", "bold"); doc.text("Nº FACTURA:", 18, resumenY);
    doc.setFont("helvetica", "normal"); doc.text(settlement.invoice_number || "S/N", 45, resumenY);
    doc.setTextColor(0, 0, 0);
    if (totalAbonos > 0 && totalAnticipos === 0) {
        doc.setFont("helvetica", "bold"); doc.text("TOTAL ABONOS:", 100, resumenY);
        doc.setTextColor(25, 135, 84); // Verde
        doc.setFont("helvetica", "normal"); doc.text(`+${formatCurrency(totalAbonos, currency)}`, 145, resumenY);
    } else if (totalAbonos > 0 && totalAnticipos > 0) {
        const netAdj = totalAbonos - totalAnticipos;
        doc.setFont("helvetica", "bold"); doc.text("AJUSTES NETOS:", 100, resumenY);
        if (netAdj >= 0) {
            doc.setTextColor(25, 135, 84);
            doc.setFont("helvetica", "normal"); doc.text(`+${formatCurrency(netAdj, currency)}`, 145, resumenY);
        } else {
            doc.setTextColor(220, 53, 69);
            doc.setFont("helvetica", "normal"); doc.text(`-${formatCurrency(Math.abs(netAdj), currency)}`, 145, resumenY);
        }
    } else {
        doc.setFont("helvetica", "bold"); doc.text("TOTAL DESCUENTOS:", 100, resumenY);
        if (totalAnticipos > 0) {
            doc.setTextColor(220, 53, 69); // Rojo
            doc.setFont("helvetica", "normal"); doc.text(`-${formatCurrency(totalAnticipos, currency)}`, 145, resumenY);
        } else {
            doc.setTextColor(0, 0, 0);
            doc.setFont("helvetica", "normal"); doc.text(formatCurrency(totalAnticipos, currency), 145, resumenY);
        }
    }
    doc.setTextColor(0, 0, 0);

    resumenY += 8;
    doc.setFont("helvetica", "bold"); doc.text("ESTADO PAGO:", 18, resumenY);
    if (settlement.is_paid) {
        doc.setTextColor(40, 167, 69);
        doc.setFont("helvetica", "bold"); doc.text("PAGADA", 45, resumenY);
    } else {
        doc.setTextColor(230, 150, 0);
        doc.setFont("helvetica", "bold"); doc.text("PENDIENTE", 45, resumenY);
    }
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text("LÍQUIDO A PAGAR:", 100, resumenY);
    doc.setTextColor(31, 78, 120);
    doc.text(formatCurrency(totalLiquido, currency), 145, resumenY);

    // --- 3. DETALLE SERVICIOS ---
    doc.setTextColor(31, 78, 120); doc.setFontSize(11);
    doc.text("DETALLE DE SERVICIOS REALIZADOS", 14, 88);

    const tableColumn = ["FECHA/HORA", "Nº RESERVA", "PASAJERO", "TRAYECTO", "OBSERVACIONES", "BASE", "ESPERA", "% COM", "PAGO"];
    
    const tableRows = trips.map(t => {
        const waitCosto = parseFloat(t.waiting_time_provider_cost) || 0;
        const cBase = currency !== 'CLP' ? (t.provider_cost_local || 0) : (t.provider_cost_base || 0);
        
        let cPago = t.driver_earnings || 0;
        const reimbursable = (t.extra_services || []).find(s => s.id === 'reimbursable_expense');
        let obsReimbursable = '';
        if (reimbursable) {
            const cost = parseFloat(reimbursable.cost) || 0;
            cPago += cost;
            obsReimbursable = ` [+Reembolso: ${formatCurrency(cost, currency)}]`;
        }
        
        let comisionPct = 0;
        const totalCostoParaComision = cBase + waitCosto;
        if (totalCostoParaComision > 0) {
            comisionPct = Math.round(((totalCostoParaComision - t.driver_earnings) / totalCostoParaComision) * 100);
        }

        let dtStr = "N/A";
        if (t.scheduled_time) {
            const dt = new Date(t.scheduled_time);
            dtStr = `${dt.getDate().toString().padStart(2, '0')}/${(dt.getMonth() + 1).toString().padStart(2, '0')}/${dt.getFullYear()} ${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}`;
        }

        const trayecto = `A: ${t.pickup_address || 'S/O'}\nB: ${t.dropoff_address || 'S/D'}`;
        
        return [
            dtStr,
            String(t.id),
            (t.passenger_name || "Particular").substring(0, 15),
            trayecto,
            ((t.notes || "-") + obsReimbursable).substring(0, 50),
            formatCurrency(cBase, currency),
            formatCurrency(waitCosto, currency),
            `${comisionPct}%`,
            formatCurrency(cPago, currency)
        ];
    });

    const tableFoot = [
        "TOTALES",
        "",
        "",
        "",
        "",
        formatCurrency(totalBaseSum, currency),
        formatCurrency(totalEsperaSum, currency),
        `${avgComision}%`,
        formatCurrency(totalPagoSum, currency)
    ];

    autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        foot: [tableFoot],
        startY: 92,
        theme: 'grid',
        headStyles: { fillColor: [242, 242, 242], textColor: [31, 78, 120], fontSize: 6.5, fontStyle: 'bold', halign: 'center' }, 
        footStyles: { fillColor: [242, 242, 242], textColor: [31, 78, 120], fontSize: 6.5, fontStyle: 'bold' },
        styles: { fontSize: 6, valign: 'middle', overflow: 'linebreak' },
        columnStyles: {
            0: { cellWidth: 25, halign: 'center' }, 
            1: { cellWidth: 15, halign: 'center' }, 
            2: { cellWidth: 20 }, 
            3: { cellWidth: 'auto' }, 
            4: { cellWidth: 20 }, 
            5: { cellWidth: 16, halign: 'right' }, 
            6: { cellWidth: 16, halign: 'right' }, 
            7: { cellWidth: 10, halign: 'center' }, 
            8: { cellWidth: 16, halign: 'right', fontStyle: 'bold' } 
        },
        alternateRowStyles: { fillColor: [255, 255, 255] }
    });

    let currentY = doc.lastAutoTable.finalY + 15;

    // --- 4. DETALLE DE AJUSTES (DESCUENTOS, PENALIZACIONES Y ABONOS) ---
    if (advancesList.length > 0) {
        if (currentY > doc.internal.pageSize.height - 60) {
            doc.addPage();
            currentY = 20;
        }

        const hasCredits = advancesList.some(a => (parseFloat(a.amount) || 0) < 0);
        const hasDebits = advancesList.some(a => (parseFloat(a.amount) || 0) > 0);
        const headerTextColor = hasCredits && !hasDebits ? [25, 135, 84] : (hasCredits && hasDebits ? [31, 78, 120] : [192, 0, 0]);
        const headerFillColor = hasCredits && !hasDebits ? [226, 239, 218] : (hasCredits && hasDebits ? [232, 240, 254] : [252, 228, 214]);
        const sectionHeading = hasCredits && !hasDebits
            ? "ABONOS Y REINTEGROS APLICADOS"
            : (hasCredits && hasDebits ? "AJUSTES APLICADOS (DESCUENTOS Y ABONOS)" : "DESCUENTOS Y PENALIZACIONES APLICADAS");

        doc.setTextColor(...headerTextColor);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(sectionHeading, 14, currentY);

        const advColumns = ["FECHA", "TIPO / CONCEPTO", "DETALLE Y REFERENCIA (OS / VIAJE)", "MONTO AJUSTE"];
        const advRows = advancesList.map(a => {
            const rawAmt = parseFloat(a.amount) || 0;
            const isCredit = rawAmt < 0;
            const notesStr = a.notes || (isCredit ? "Abono / Reintegro Booking" : "Anticipo de caja");
            const isPenalty = !isCredit && (notesStr.toLowerCase().includes('disputa') || notesStr.toLowerCase().includes('booking') || notesStr.toLowerCase().includes('penalizaci'));
            const tipo = isCredit ? "Abono / Reintegro Booking" : (isPenalty ? "Penalización Booking" : "Anticipo de Caja");
            
            let fStr = "N/A";
            if (a.created_at) fStr = new Date(a.created_at).toLocaleDateString('es-CL');
            else if (a.start_date) fStr = formatSettleDate(a.start_date);

            return [
                fStr,
                tipo,
                notesStr,
                isCredit ? `+${formatCurrency(Math.abs(rawAmt), currency)}` : `-${formatCurrency(rawAmt, currency)}`
            ];
        });

        autoTable(doc, {
            head: [advColumns],
            body: advRows,
            startY: currentY + 4,
            theme: 'grid',
            headStyles: { fillColor: headerFillColor, textColor: headerTextColor, fontSize: 7, fontStyle: 'bold' },
            styles: { fontSize: 7, valign: 'middle', overflow: 'linebreak' },
            columnStyles: {
                0: { cellWidth: 25, halign: 'center' },
                1: { cellWidth: 35, fontStyle: 'bold' },
                2: { cellWidth: 'auto' },
                3: { cellWidth: 30, halign: 'right', fontStyle: 'bold' }
            },
            didParseCell: (data) => {
                if (data.section === 'body' && (data.column.index === 1 || data.column.index === 3)) {
                    const rowAmt = parseFloat(advancesList[data.row.index]?.amount) || 0;
                    data.cell.styles.textColor = rowAmt < 0 ? [25, 135, 84] : [192, 0, 0];
                }
            }
        });

        currentY = doc.lastAutoTable.finalY + 15;
    }

    // --- 5. CUADRO RESUMEN FINAL Y LÍQUIDO A RECIBIR ---
    const summaryBoxHeight = totalAbonos > 0 ? 40 : 32;
    if (currentY > doc.internal.pageSize.height - (summaryBoxHeight + 18)) {
        doc.addPage();
        currentY = 20;
    }

    const boxX = pageWidth - 120;
    doc.setFillColor(248, 249, 250);
    doc.setDrawColor(200, 200, 200);
    doc.rect(boxX, currentY, 106, summaryBoxHeight, 'FD');

    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 0, 0);
    doc.text("TOTAL GANANCIA SERVICIOS:", boxX + 4, currentY + 7);
    doc.setFont("helvetica", "normal");
    doc.text(formatCurrency(totalFacturarChofer, currency), boxX + 100, currentY + 7, { align: 'right' });

    doc.setFont("helvetica", "bold"); doc.setTextColor(192, 0, 0);
    doc.text("TOTAL DESCUENTOS/PENALIZACIONES:", boxX + 4, currentY + 15);
    doc.setFont("helvetica", "normal");
    doc.text(`-${formatCurrency(totalAnticipos, currency)}`, boxX + 100, currentY + 15, { align: 'right' });

    let liquidoBoxY = currentY + 20;
    if (totalAbonos > 0) {
        doc.setFont("helvetica", "bold"); doc.setTextColor(25, 135, 84);
        doc.text("TOTAL ABONOS / REINTEGROS:", boxX + 4, currentY + 23);
        doc.setFont("helvetica", "normal");
        doc.text(`+${formatCurrency(totalAbonos, currency)}`, boxX + 100, currentY + 23, { align: 'right' });
        liquidoBoxY = currentY + 28;
    }

    doc.setFillColor(226, 239, 218); // Verde claro destacado
    doc.rect(boxX + 2, liquidoBoxY, 102, 9, 'F');
    doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(31, 78, 120);
    doc.text("TOTAL LÍQUIDO A RECIBIR:", boxX + 4, liquidoBoxY + 6);
    doc.text(formatCurrency(totalLiquido, currency), boxX + 100, liquidoBoxY + 6, { align: 'right' });

    currentY += summaryBoxHeight + 13;

    // --- 6. FIRMA Y PIE DE PÁGINA ---
    if (currentY > doc.internal.pageSize.height - 35) {
        doc.addPage(); currentY = doc.internal.pageSize.height - 45;
    }

    doc.setDrawColor(180);
    doc.line(pageWidth / 2 - 30, currentY, pageWidth / 2 + 30, currentY);
    doc.setFontSize(8); doc.setTextColor(0, 0, 0);
    doc.text(`Firma Conforme: ${driverName}`, pageWidth / 2, currentY + 5, { align: 'center' });

    const footerY = doc.internal.pageSize.height - 15;
    try {
        const emigoImg = await loadImage('/EmiGo.png');
        doc.addImage(emigoImg, 'PNG', 14, footerY - 5, 12, 12);
    } catch (e) {
        console.warn("Logo EmiGo no pudo ser cargado al final del PDF:", e);
    }
    
    const now = new Date();
    const emissionStr = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    doc.setTextColor(150, 150, 150); doc.setFontSize(7);
    doc.text(`Fecha y Hora de Emisión: ${emissionStr}`, 28, footerY + 2, { align: 'left' });
    doc.text("Documento generado por Sistema EmiGo.", 28, footerY + 6, { align: 'left' });

    doc.save(`Liquidacion_${folio}.pdf`);
};